import CreateServiceAgreementCustomerModal from '../../components/ServiceAgreement/Customer/CreateServiceAgreementCustomerModal';
import { invalidateCustomerSuggestions } from '../../hooks/queries/migration.queries';
import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';

interface Props {
  suggestion: MigrationCandidateBusinessPartnerBody;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

/**
 * **Add as new customer**: resolves a Customer Suggestion that matched nothing
 * worth merging into, by creating a brand-new customer from its data.
 *
 * Deliberately not a new wizard — it is the dealer's own
 * `CreateServiceAgreementCustomerModal` (Organization type + General
 * information + Contact information + Customer number → Service agreement type
 * → Terms & Conditions) pre-filled from the suggestion, with the "Add existing
 * customer" tab dropped: the user already decided none of the matches fit.
 * This mirrors `AddOrganizationFromMigrationCandidateModal` on the identity
 * side.
 *
 * **Candidate resolution.** No accept/reject call is made here. The dealer
 * accept endpoint only takes an `externalId` drawn from the candidate's
 * `matchDetails.top_targets`, and a brand-new customer has no such target; the
 * candidate's own `id` is passed through as
 * `migrationCandidateBusinessPartnerId` on the proxy request body instead, so
 * the backend can link the two and transition the candidate once the new
 * customer request exists, exactly as on the identity side. The front end
 * therefore only refreshes the list. If that assumption turns out to be wrong
 * the suggestion lingers until the next import — visible, and recoverable,
 * unlike overloading `reject` to mean "resolved by creating a customer", which
 * would silently corrupt the audit trail.
 */
function AddCustomerFromSuggestionModal({ suggestion, open, onClose }: Props) {
  const handleClose = (shouldUpdate: boolean) => {
    if (shouldUpdate) invalidateCustomerSuggestions();
    onClose(shouldUpdate);
  };

  // Mounted only while a suggestion is being added, because the customer form
  // seeds its state on mount only: a persistent mount would ignore the prefill
  // (the identity-side modal needed the same fix).
  if (!open) return null;

  return (
    <CreateServiceAgreementCustomerModal
      open={open}
      onClose={handleClose}
      defaultValues={{ name: suggestion.name, ...suggestion.address }}
      allowExistingCustomer={false}
      showVerifyBanner
      migrationCandidateBusinessPartnerId={suggestion.id}
    />
  );
}

export default AddCustomerFromSuggestionModal;
